import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock } from 'lucide-react';

interface OccasionCardProps {
  occasion: {
    id: string;
    name: string;
    slug: string;
    communityMonth: number | null;
    communityDay: number | null;
    durationDays: number | null;
    startDate: Date | null;
    endDate: Date | null;
    description: string | null;
    imageUrl: string | null;
    occasionType: {
      label: string;
      icon: string | null;
    };
  };
}

export function OccasionCard({ occasion }: OccasionCardProps) {
  const formatDate = (date: Date | null) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getcommunityMonthName = (month: number) => {
    const months = [
      'Muharram', 'Safar', 'Rabi al-Awwal', 'Rabi al-Thani',
      'Jumada al-Awwal', 'Jumada al-Thani', 'Rajab', 'Shaban',
      'Ramadan', 'Shawwal', 'Dhul Qadah', 'Dhul Hijjah'
    ];
    return months[month - 1] || '';
  };

  const isActive = () => {
    if (!occasion.startDate || !occasion.endDate) return false;
    const now = new Date();
    return now >= new Date(occasion.startDate) && now <= new Date(occasion.endDate);
  };

  const isUpcoming = () => {
    if (!occasion.startDate) return false;
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    return new Date(occasion.startDate) > now && new Date(occasion.startDate) <= thirtyDaysFromNow;
  };

  return (
    <Link
      to={`/occasions/${occasion.slug}`}
      className="block bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 overflow-hidden border border-gray-200 dark:border-gray-700"
    >
      {occasion.imageUrl && (
        <div className="h-40 w-full overflow-hidden">
          <img
            src={occasion.imageUrl}
            alt={occasion.name}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="p-5">
        {/* Status Badge */}
        {isActive() && (
          <span className="inline-block mb-2 px-3 py-1 text-xs font-semibold text-white bg-green-600 rounded-full">
            Active Now
          </span>
        )}
        {isUpcoming() && !isActive() && (
          <span className="inline-block mb-2 px-3 py-1 text-xs font-semibold text-white bg-blue-600 rounded-full">
            Upcoming
          </span>
        )}

        {/* Occasion Type */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xl">{occasion.occasionType.icon}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {occasion.occasionType.label}
          </span>
        </div>

        {/* Occasion Name */}
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          {occasion.name}
        </h3>

        {/* Description */}
        {occasion.description && (
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
            {occasion.description}
          </p>
        )}

        {/* Community Date */}
        {occasion.communityMonth && (
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
            <Calendar className="w-4 h-4" />
            <span>
              {getcommunityMonthName(occasion.communityMonth)}
              {occasion.communityDay && ` ${occasion.communityDay}`}
            </span>
          </div>
        )}

        {/* Gregorian Dates */}
        {occasion.startDate && (
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <Clock className="w-4 h-4" />
            <span>
              {formatDate(occasion.startDate)}
              {occasion.endDate && occasion.startDate !== occasion.endDate && (
                <> - {formatDate(occasion.endDate)}</>
              )}
            </span>
          </div>
        )}

        {/* Duration */}
        {occasion.durationDays && occasion.durationDays > 1 && (
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Duration: {occasion.durationDays} days
          </div>
        )}
      </div>
    </Link>
  );
}



